import type {
  PaidMediaOverviewAttributionConfidenceData,
  PaidMediaOverviewAttributionMode,
  PaidMediaOverviewCampaignBenchmarks,
  PaidMediaOverviewCampaignDetailData,
  PaidMediaOverviewCampaignRow,
  PaidMediaOverviewCampaignsData,
  PaidMediaOverviewEfficiencyScaleData,
  PaidMediaOverviewFiltersData,
  PaidMediaOverviewFunnelData,
  PaidMediaOverviewFunnelPlatform,
  PaidMediaOverviewGranularity,
  PaidMediaOverviewPlatform,
  PaidMediaOverviewPlatformFilter,
  PaidMediaOverviewQuadrant,
  PaidMediaOverviewRevenueValidationData,
  PaidMediaOverviewRevenueValidationPlatform,
  PaidMediaOverviewScalePoint,
  PaidMediaOverviewSortOrder,
  PaidMediaOverviewSummaryData,
  PaidMediaOverviewSummaryKpi,
  PaidMediaOverviewSummaryPlatform,
  PaidMediaOverviewTrendMetrics,
  PaidMediaOverviewTrendPoint,
  PaidMediaOverviewTrendsData,
} from "src/services/PaidMediaOverviewAPI";
import {
  OVERVIEW_PLATFORM_DESCRIPTORS,
  OVERVIEW_PLATFORM_KEYS,
  overviewPlatformDescriptor,
} from "../data/paidMediaOverviewPlatforms";
import {
  buildMockAttributionConfidence,
  buildMockCampaignDailyFacts,
  buildMockCampaignMetrics,
  buildMockDailyFacts,
  bucketMockFacts,
  MOCK_CAMPAIGN_SEEDS,
  MOCK_VALIDATION_PROFILES,
  mockPlatformTotals,
  mockPriorPeriod,
  PAID_MEDIA_MOCK_CURRENCY,
  sumMockFacts,
  type MockDailyFact,
  type MockTotals,
  type MockValidationProfile,
} from "../data/paidMediaOverviewMockData";

/**
 * Composes the four-platform overview: Meta and Google keep their live
 * endpoints, TikTok and Snapchat come from the mock dataset, and every derived
 * number (shares, blended KPIs, leaders) is recomputed here so the two halves
 * add up. Two consequences: `formatted_*` is always `null` because the API
 * formats money in the account currency and components format the raw numbers
 * in SAR instead, and a live platform with no data falls back to its mock
 * profile so the dashboard always renders four platforms.
 */

export const OVERVIEW_CURRENCY = PAID_MEDIA_MOCK_CURRENCY;

/**
 * Live amounts are relabelled as SAR rather than converted: the demo account's
 * numeric levels already match the mock platforms, so applying a real FX rate
 * would shrink Meta and Google to a few percent of total spend. Set a real rate
 * here if the live account currency ever needs converting.
 */
export const API_MONEY_TO_OVERVIEW_CURRENCY_RATE = 1;

export interface OverviewCompositionContext {
  startDate: string;
  endDate: string;
  granularity: PaidMediaOverviewGranularity;
  platformFilter: PaidMediaOverviewPlatformFilter;
  attributionMode: PaidMediaOverviewAttributionMode;
  campaignId: string;
  objectiveOrNetwork: string;
  status: string;
}

export interface PlatformAggregate {
  platform: PaidMediaOverviewPlatform;
  /** Whether the live endpoint supplied these numbers or the mock did. */
  source: "api" | "mock";
  spend: number;
  revenue: number;
  purchases: number;
  impressions: number;
  clicks: number;
}

interface PeriodTotals {
  spend: number;
  revenue: number;
  purchases: number;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const finite = (value: number | null | undefined): number | null =>
  value !== null && value !== undefined && Number.isFinite(value) ? value : null;

const ratio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? round2(numerator / denominator) : null;

const share = (part: number, whole: number): number | null =>
  whole > 0 ? round2((part / whole) * 100) : null;

const percent = (part: number, whole: number): number | null =>
  whole > 0 ? round2((part / whole) * 100) : null;

/** Converts a live money value into the currency the overview renders. */
const apiMoney = (value: number | null | undefined): number | null => {
  const amount = finite(value);
  return amount === null ? null : round2(amount * API_MONEY_TO_OVERVIEW_CURRENCY_RATE);
};

/* ---------------- attribution ---------------- */

/** Store-validated mode discounts revenue and purchases; spend is never re-stated. */
const attributionScale = (
  platform: PaidMediaOverviewPlatform,
  mode: PaidMediaOverviewAttributionMode
): number =>
  mode === "shopify_validated"
    ? 1 - (MOCK_VALIDATION_PROFILES[platform]?.overReporting ?? 0)
    : 1;

/* ---------------- platform selection ---------------- */

export function activeOverviewPlatforms(
  platformFilter: PaidMediaOverviewPlatformFilter
): PaidMediaOverviewPlatform[] {
  if (!platformFilter || platformFilter === "all") {
    return [...OVERVIEW_PLATFORM_KEYS];
  }
  return OVERVIEW_PLATFORM_KEYS.filter(platform => platform === platformFilter);
}

/* ---------------- mock campaign universe ---------------- */

export interface MockCampaignRow {
  platform: PaidMediaOverviewPlatform;
  campaignId: string;
  campaignName: string;
  objectiveOrNetwork: string;
  startDate: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
  roas: number | null;
  statusCode: string;
  quadrant: PaidMediaOverviewQuadrant;
}

const percentile = (values: number[], fraction: number): number | null => {
  const sorted = [...values].filter(Number.isFinite).sort((left, right) => left - right);
  if (!sorted.length) {
    return null;
  }
  const index = Math.min(sorted.length - 1, Math.floor(fraction * (sorted.length - 1)));
  return sorted[index];
};

const quadrantFor = (
  roas: number | null,
  spend: number,
  roasBenchmark: number | null,
  spendBenchmark: number | null
): PaidMediaOverviewQuadrant => {
  const aboveRoas = roas !== null && roasBenchmark !== null && roas >= roasBenchmark;
  const aboveSpend = spendBenchmark !== null && spend >= spendBenchmark;

  if (aboveRoas) {
    return aboveSpend ? "SCALE_WINNER" : "GROWTH_OPPORTUNITY";
  }
  return aboveSpend ? "HIGH_SPEND_LOW_RETURN" : "LOW_PRIORITY";
};

/** Finer-grained than the four quadrants: comfortably above benchmark wins. */
const statusCodeFor = (
  roas: number | null,
  spend: number,
  roasBenchmark: number | null,
  spendBenchmark: number | null
): string => {
  if (roas === null || roasBenchmark === null) {
    return "STABLE";
  }
  const aboveSpend = spendBenchmark !== null && spend >= spendBenchmark;

  if (roas >= roasBenchmark * 1.25) {
    return aboveSpend ? "SCALE_WINNER" : "EFFICIENCY_WINNER";
  }
  if (roas >= roasBenchmark) {
    return aboveSpend ? "STABLE" : "GROWTH_OPPORTUNITY";
  }
  if (roas >= roasBenchmark * 0.6) {
    return aboveSpend ? "NEEDS_REVIEW" : "STABLE";
  }
  return aboveSpend ? "HIGH_SPEND_LOW_RETURN" : "LOW_PRIORITY";
};

export interface MockCampaignUniverse {
  rows: MockCampaignRow[];
  roasBenchmark: number | null;
  spendBenchmark: number | null;
  spendMedian: number | null;
}

/** Classified against the unfiltered set, so filtering never re-scores rows. */
export function buildMockCampaignUniverse(
  context: OverviewCompositionContext,
  platforms: PaidMediaOverviewPlatform[]
): MockCampaignUniverse {
  const raw = platforms.flatMap(platform => {
    const totals = mockPlatformTotals(platform, context.startDate, context.endDate);
    const scale = attributionScale(platform, context.attributionMode);
    return buildMockCampaignMetrics(platform, totals).map(campaign => ({
      ...campaign,
      purchases: Math.round(campaign.purchases * scale),
      revenue: round2(campaign.revenue * scale),
    }));
  });

  const totalSpend = raw.reduce((sum, campaign) => sum + campaign.spend, 0);
  const totalRevenue = raw.reduce((sum, campaign) => sum + campaign.revenue, 0);
  const roasBenchmark = ratio(totalRevenue, totalSpend);
  const spendBenchmark = percentile(
    raw.map(campaign => campaign.spend),
    0.75
  );
  const spendMedian = percentile(
    raw.map(campaign => campaign.spend),
    0.5
  );

  const rows: MockCampaignRow[] = raw.map(campaign => {
    const roas = ratio(campaign.revenue, campaign.spend);
    return {
      platform: campaign.platform,
      campaignId: campaign.campaignId,
      campaignName: campaign.campaignName,
      objectiveOrNetwork: campaign.objectiveOrNetwork,
      startDate: campaign.startDate,
      spend: campaign.spend,
      impressions: campaign.impressions,
      clicks: campaign.clicks,
      purchases: campaign.purchases,
      revenue: campaign.revenue,
      roas,
      statusCode: statusCodeFor(roas, campaign.spend, roasBenchmark, spendBenchmark),
      quadrant: quadrantFor(roas, campaign.spend, roasBenchmark, spendBenchmark),
    };
  });

  return { rows, roasBenchmark, spendBenchmark, spendMedian };
}

export function filterMockCampaignRows(
  rows: MockCampaignRow[],
  context: OverviewCompositionContext,
  search?: string
): MockCampaignRow[] {
  const needle = search?.trim().toLowerCase() ?? "";

  return rows.filter(row => {
    if (context.platformFilter !== "all" && row.platform !== context.platformFilter) {
      return false;
    }
    if (context.campaignId && row.campaignId !== context.campaignId) {
      return false;
    }
    if (context.objectiveOrNetwork && row.objectiveOrNetwork !== context.objectiveOrNetwork) {
      return false;
    }
    if (context.status && row.statusCode !== context.status) {
      return false;
    }
    if (needle && !row.campaignName.toLowerCase().includes(needle)) {
      return false;
    }
    return true;
  });
}

/* ---------------- platform aggregates ---------------- */

interface RowTotals {
  spend: number;
  revenue: number;
  purchases: number;
  impressions: number;
  clicks: number;
}

const sumRows = (rows: MockCampaignRow[]): RowTotals =>
  rows.reduce(
    (totals, row) => ({
      spend: round2(totals.spend + row.spend),
      revenue: round2(totals.revenue + row.revenue),
      purchases: totals.purchases + row.purchases,
      impressions: totals.impressions + row.impressions,
      clicks: totals.clicks + row.clicks,
    }),
    { spend: 0, revenue: 0, purchases: 0, impressions: 0, clicks: 0 }
  );

export interface OverviewApiSlice {
  summary: PaidMediaOverviewSummaryData | null;
  funnel: PaidMediaOverviewFunnelData | null;
}

/** A live platform is used only when it reported spend; otherwise the mock stands in. */
export function buildPlatformAggregates(
  context: OverviewCompositionContext,
  api: OverviewApiSlice,
  universe: MockCampaignUniverse
): PlatformAggregate[] {
  const platforms = activeOverviewPlatforms(context.platformFilter);
  const filteredRows = filterMockCampaignRows(universe.rows, context);

  return platforms
    .map<PlatformAggregate | null>(platform => {
      const descriptor = overviewPlatformDescriptor(platform);
      const mock = sumRows(filteredRows.filter(row => row.platform === platform));

      const apiPlatform = descriptor?.mockOnly
        ? undefined
        : api.summary?.platforms?.find(entry => entry?.platform === platform);
      const apiFunnel = descriptor?.mockOnly
        ? undefined
        : api.funnel?.platforms?.find(entry => entry?.platform === platform);

      const apiSpend = apiMoney(apiPlatform?.spend);
      if (apiSpend !== null && apiSpend > 0) {
        return {
          platform,
          source: "api",
          spend: apiSpend,
          revenue: apiMoney(apiPlatform?.revenue) ?? 0,
          purchases: finite(apiPlatform?.purchases) ?? 0,
          // The summary endpoint has no reach metrics; the funnel endpoint does.
          impressions: finite(apiFunnel?.impressions) ?? mock.impressions,
          clicks: finite(apiFunnel?.clicks) ?? mock.clicks,
        };
      }

      if (mock.spend <= 0) {
        return null;
      }

      return { platform, source: "mock", ...mock };
    })
    .filter((aggregate): aggregate is PlatformAggregate => aggregate !== null);
}

const totalsOf = (aggregates: PlatformAggregate[]): PeriodTotals => ({
  spend: round2(aggregates.reduce((sum, item) => sum + item.spend, 0)),
  revenue: round2(aggregates.reduce((sum, item) => sum + item.revenue, 0)),
  purchases: aggregates.reduce((sum, item) => sum + item.purchases, 0),
});

/* ---------------- prior period ---------------- */

const mockPriorTotals = (
  context: OverviewCompositionContext,
  platforms: PaidMediaOverviewPlatform[]
): PeriodTotals => {
  const prior = mockPriorPeriod(context.startDate, context.endDate);
  return platforms.reduce<PeriodTotals>(
    (totals, platform) => {
      const period = mockPlatformTotals(platform, prior.startDate, prior.endDate);
      const scale = attributionScale(platform, context.attributionMode);
      return {
        spend: round2(totals.spend + period.spend),
        revenue: round2(totals.revenue + period.revenue * scale),
        purchases: totals.purchases + Math.round(period.purchases * scale),
      };
    },
    { spend: 0, revenue: 0, purchases: 0 }
  );
};

const KPI_IDS = {
  spend: "total-paid-media-spend",
  revenue: "paid-media-revenue",
  purchases: "paid-purchases",
  roas: "blended-roas",
  costPerPurchase: "blended-cost-per-purchase",
} as const;

/** The live summary's `previous_value` plus the mocked platforms' prior window. */
function buildPriorTotals(
  context: OverviewCompositionContext,
  aggregates: PlatformAggregate[],
  apiSummary: PaidMediaOverviewSummaryData | null
): PeriodTotals {
  const apiPlatforms = aggregates.filter(aggregate => aggregate.source === "api");
  const mockPlatforms = aggregates
    .filter(aggregate => aggregate.source === "mock")
    .map(aggregate => aggregate.platform);

  const mockPrior = mockPriorTotals(context, mockPlatforms);

  if (!apiPlatforms.length) {
    return mockPrior;
  }

  const previousOf = (id: string): number | null => {
    const kpi = apiSummary?.combined?.find(entry => entry?.id === id);
    return finite(kpi?.previous_value);
  };

  const apiPriorSpend = apiMoney(previousOf(KPI_IDS.spend));
  const apiPriorRevenue = apiMoney(previousOf(KPI_IDS.revenue));
  const apiPriorPurchases = previousOf(KPI_IDS.purchases);

  // If the live summary carries no baseline, fall back to the mock profile for
  // those platforms so the KPI cards still show a comparison.
  const apiFallback = mockPriorTotals(
    context,
    apiPlatforms.map(aggregate => aggregate.platform)
  );

  return {
    spend: round2(mockPrior.spend + (apiPriorSpend ?? apiFallback.spend)),
    revenue: round2(mockPrior.revenue + (apiPriorRevenue ?? apiFallback.revenue)),
    purchases: mockPrior.purchases + Math.round(apiPriorPurchases ?? apiFallback.purchases),
  };
}

/* ---------------- summary ---------------- */

const changePercent = (current: number | null, previous: number | null): number | null => {
  if (current === null || previous === null || previous === 0) {
    return null;
  }
  return round2(((current - previous) / Math.abs(previous)) * 100);
};

const kpi = (
  id: string,
  title: string,
  value: number | null,
  previous: number | null
): PaidMediaOverviewSummaryKpi => ({
  id,
  title,
  value,
  formatted_value: null,
  previous_value: previous,
  percent_change: changePercent(value, previous),
  formatted_percent_change: null,
});

export function composeOverviewSummary(
  context: OverviewCompositionContext,
  aggregates: PlatformAggregate[],
  apiSummary: PaidMediaOverviewSummaryData | null
): PaidMediaOverviewSummaryData {
  const totals = totalsOf(aggregates);
  const prior = buildPriorTotals(context, aggregates, apiSummary);

  const platforms: PaidMediaOverviewSummaryPlatform[] = aggregates.map(aggregate => {
    const spendShare = share(aggregate.spend, totals.spend);
    const revenueShare = share(aggregate.revenue, totals.revenue);

    return {
      platform: aggregate.platform,
      available: true,
      spend: aggregate.spend,
      formatted_spend: null,
      revenue: aggregate.revenue,
      formatted_revenue: null,
      purchases: aggregate.purchases,
      formatted_purchases: null,
      roas: ratio(aggregate.revenue, aggregate.spend),
      formatted_roas: null,
      cost_per_purchase: ratio(aggregate.spend, aggregate.purchases),
      formatted_cost_per_purchase: null,
      spend_share: spendShare,
      formatted_spend_share: null,
      revenue_share: revenueShare,
      formatted_revenue_share: null,
      purchase_share: share(aggregate.purchases, totals.purchases),
      formatted_purchase_share: null,
      efficiency_index:
        spendShare !== null && revenueShare !== null && spendShare > 0
          ? round2(revenueShare / spendShare)
          : null,
      formatted_efficiency_index: null,
    };
  });

  const ranked = [...platforms].sort((left, right) => (right.roas ?? 0) - (left.roas ?? 0));
  const leader = ranked[0] ?? null;
  const laggard = ranked[ranked.length - 1] ?? null;

  const revenueLeader = [...platforms].sort(
    (left, right) => (right.efficiency_index ?? 0) - (left.efficiency_index ?? 0)
  )[0];

  return {
    combined: [
      kpi(KPI_IDS.spend, "Total Paid Media Spend", totals.spend, prior.spend),
      kpi(KPI_IDS.revenue, "Paid Media Revenue", totals.revenue, prior.revenue),
      kpi(KPI_IDS.purchases, "Paid Purchases", totals.purchases, prior.purchases),
      kpi(
        KPI_IDS.roas,
        "Blended ROAS",
        ratio(totals.revenue, totals.spend),
        ratio(prior.revenue, prior.spend)
      ),
      kpi(
        KPI_IDS.costPerPurchase,
        "Blended Cost per Purchase",
        ratio(totals.spend, totals.purchases),
        ratio(prior.spend, prior.purchases)
      ),
    ],
    leading_platform: leader
      ? { platform: leader.platform, roas: leader.roas, reason: null }
      : null,
    revenue_contribution_leader: revenueLeader
      ? {
          platform: revenueLeader.platform,
          revenue_share: revenueLeader.revenue_share,
          spend_share: revenueLeader.spend_share,
          efficiency_index: revenueLeader.efficiency_index,
        }
      : null,
    efficiency_gap:
      leader && laggard && leader.roas !== null && laggard.roas !== null && leader !== laggard
        ? { value: round2(leader.roas - laggard.roas), leading_platform: leader.platform }
        : null,
    platforms,
  };
}

/** Lowest-ROAS platform of the period — the other half of the efficiency gap. */
export function overviewEfficiencyLaggard(
  summary: PaidMediaOverviewSummaryData | null
): PaidMediaOverviewPlatform | null {
  const withRoas = (summary?.platforms ?? []).filter(platform => finite(platform?.roas) !== null);
  if (withRoas.length < 2) {
    return null;
  }
  return withRoas.reduce((lowest, platform) =>
    (platform.roas ?? 0) < (lowest.roas ?? 0) ? platform : lowest
  ).platform;
}

/* ---------------- trends ---------------- */

const trendMetrics = (fact: MockDailyFact | null): PaidMediaOverviewTrendMetrics | null => {
  if (!fact) {
    return null;
  }
  return {
    spend: fact.spend,
    formatted_spend: null,
    revenue: fact.revenue,
    formatted_revenue: null,
    purchases: fact.purchases,
    formatted_purchases: null,
    roas: ratio(fact.revenue, fact.spend),
    formatted_roas: null,
    cost_per_purchase: ratio(fact.spend, fact.purchases),
    formatted_cost_per_purchase: null,
  };
};

const scaleFact = (fact: MockDailyFact, spendRatio: number, revenueRatio: number): MockDailyFact => ({
  date: fact.date,
  spend: round2(fact.spend * spendRatio),
  impressions: Math.round(fact.impressions * spendRatio),
  clicks: Math.round(fact.clicks * spendRatio),
  purchases: Math.round(fact.purchases * revenueRatio),
  revenue: round2(fact.revenue * revenueRatio),
});

/** Scaled to the filtered totals, so the chart sums back to the platform card. */
function mockSeriesFor(
  platform: PaidMediaOverviewPlatform,
  context: OverviewCompositionContext,
  aggregate: PlatformAggregate
): MockDailyFact[] {
  const facts = buildMockDailyFacts(platform, context.startDate, context.endDate);
  const unfiltered = sumMockFacts(facts);
  const scale = attributionScale(platform, context.attributionMode);

  const spendRatio = unfiltered.spend > 0 ? aggregate.spend / unfiltered.spend : 0;
  const revenueRatio =
    unfiltered.revenue > 0 ? aggregate.revenue / (unfiltered.revenue * scale) : 0;

  return facts.map(fact =>
    scaleFact(fact, spendRatio, revenueRatio * scale)
  );
}

export function composeOverviewTrends(
  context: OverviewCompositionContext,
  aggregates: PlatformAggregate[],
  apiTrends: PaidMediaOverviewTrendsData | null
): PaidMediaOverviewTrendsData {
  const buckets = new Map<string, PaidMediaOverviewTrendPoint>();

  const ensureBucket = (date: string): PaidMediaOverviewTrendPoint => {
    const existing = buckets.get(date);
    if (existing) {
      return existing;
    }
    const created: PaidMediaOverviewTrendPoint = {
      date,
      meta_ads: null,
      google_ads: null,
      tiktok_ads: null,
      snapchat_ads: null,
      combined: null,
      leader: null,
      leader_reason: null,
    };
    buckets.set(date, created);
    return created;
  };

  const apiSeries = apiTrends?.series ?? [];
  const apiPlatforms = new Set(
    aggregates.filter(aggregate => aggregate.source === "api").map(aggregate => aggregate.platform)
  );

  apiSeries.forEach(point => {
    if (!point?.date) {
      return;
    }
    const bucket = ensureBucket(point.date);
    apiPlatforms.forEach(platform => {
      const metrics = point[platform] ?? null;
      if (metrics) {
        bucket[platform] = {
          ...metrics,
          spend: apiMoney(metrics.spend),
          revenue: apiMoney(metrics.revenue),
          cost_per_purchase: apiMoney(metrics.cost_per_purchase),
          formatted_spend: null,
          formatted_revenue: null,
          formatted_purchases: null,
          formatted_roas: null,
          formatted_cost_per_purchase: null,
        };
      }
    });
  });

  aggregates
    .filter(aggregate => !apiPlatforms.has(aggregate.platform))
    .forEach(aggregate => {
      const series = bucketMockFacts(
        mockSeriesFor(aggregate.platform, context, aggregate),
        context.granularity
      );
      series.forEach(fact => {
        ensureBucket(fact.date)[aggregate.platform] = trendMetrics(fact);
      });
    });

  const series = Array.from(buckets.values())
    .sort((left, right) => left.date.localeCompare(right.date))
    .map(point => {
      const perPlatform = OVERVIEW_PLATFORM_KEYS.map(platform => ({
        platform,
        metrics: point[platform] ?? null,
      })).filter(entry => entry.metrics !== null);

      const spend = perPlatform.reduce((sum, entry) => sum + (entry.metrics?.spend ?? 0), 0);
      const revenue = perPlatform.reduce((sum, entry) => sum + (entry.metrics?.revenue ?? 0), 0);
      const purchases = perPlatform.reduce(
        (sum, entry) => sum + (entry.metrics?.purchases ?? 0),
        0
      );

      const leader = perPlatform.reduce<{ platform: PaidMediaOverviewPlatform; roas: number } | null>(
        (best, entry) => {
          const roas = finite(entry.metrics?.roas);
          if (roas === null) {
            return best;
          }
          return !best || roas > best.roas ? { platform: entry.platform, roas } : best;
        },
        null
      );

      return {
        ...point,
        combined: perPlatform.length
          ? {
              spend: round2(spend),
              formatted_spend: null,
              revenue: round2(revenue),
              formatted_revenue: null,
              purchases,
              formatted_purchases: null,
              roas: ratio(revenue, spend),
              formatted_roas: null,
              cost_per_purchase: ratio(spend, purchases),
              formatted_cost_per_purchase: null,
            }
          : null,
        leader: leader?.platform ?? null,
        leader_reason: null,
      };
    });

  return { granularity: context.granularity, series };
}

/* ---------------- funnel ---------------- */

export function composeOverviewFunnel(
  aggregates: PlatformAggregate[]
): PaidMediaOverviewFunnelData {
  const platforms: PaidMediaOverviewFunnelPlatform[] = aggregates.map(aggregate => ({
    platform: aggregate.platform,
    available: true,
    impressions: aggregate.impressions,
    formatted_impressions: null,
    clicks: aggregate.clicks,
    formatted_clicks: null,
    purchases: aggregate.purchases,
    formatted_purchases: null,
    ctr: percent(aggregate.clicks, aggregate.impressions),
    formatted_ctr: null,
    click_to_purchase_rate: percent(aggregate.purchases, aggregate.clicks),
    formatted_click_to_purchase_rate: null,
  }));

  return { platforms };
}

/* ---------------- efficiency vs scale ---------------- */

export function composeEfficiencyScale(
  aggregates: PlatformAggregate[],
  universe: MockCampaignUniverse,
  context: OverviewCompositionContext,
  apiCampaignPoints: PaidMediaOverviewScalePoint[]
): PaidMediaOverviewEfficiencyScaleData {
  const totals = totalsOf(aggregates);
  const roasBenchmark = ratio(totals.revenue, totals.spend);
  // Average platform spend, not the median: with four platforms the median sits
  // exactly on one of them, which would always place it above the benchmark.
  const spendBenchmark = aggregates.length
    ? round2(totals.spend / aggregates.length)
    : null;

  const platform_view: PaidMediaOverviewScalePoint[] = aggregates.map(aggregate => {
    const roas = ratio(aggregate.revenue, aggregate.spend);
    return {
      platform: aggregate.platform,
      label: overviewPlatformDescriptor(aggregate.platform)?.slug ?? aggregate.platform,
      campaign_id: null,
      spend: aggregate.spend,
      formatted_spend: null,
      revenue: aggregate.revenue,
      formatted_revenue: null,
      roas,
      formatted_roas: null,
      quadrant: quadrantFor(roas, aggregate.spend, roasBenchmark, spendBenchmark),
    };
  });

  const apiPlatforms = new Set(
    aggregates.filter(aggregate => aggregate.source === "api").map(aggregate => aggregate.platform)
  );

  const livePoints = (apiCampaignPoints ?? [])
    .filter(point => apiPlatforms.has(point?.platform))
    .map(point => ({
      ...point,
      spend: apiMoney(point.spend),
      revenue: apiMoney(point.revenue),
    }));
  const mockPoints = filterMockCampaignRows(universe.rows, context)
    .filter(row => !apiPlatforms.has(row.platform))
    .map(row => ({
      platform: row.platform,
      label: row.campaignName,
      campaign_id: row.campaignId,
      spend: row.spend,
      formatted_spend: null,
      revenue: row.revenue,
      formatted_revenue: null,
      roas: row.roas,
      formatted_roas: null,
      quadrant: null,
    }));

  const merged = [...livePoints, ...mockPoints];
  const campaignSpendBenchmark = percentile(
    merged.map(point => finite(point.spend) ?? 0),
    0.75
  );

  const campaign_view: PaidMediaOverviewScalePoint[] = merged.map(point => ({
    ...point,
    formatted_spend: null,
    formatted_revenue: null,
    formatted_roas: null,
    quadrant: quadrantFor(
      finite(point.roas),
      finite(point.spend) ?? 0,
      roasBenchmark,
      campaignSpendBenchmark
    ),
  }));

  return {
    platform_view,
    campaign_view,
    benchmarks: {
      spend_benchmark: spendBenchmark,
      roas_benchmark: roasBenchmark,
      roas_benchmark_source: "blended_period_roas",
    },
  };
}

/* ---------------- revenue validation ---------------- */

/** Reuses a live platform's own variance ratios, applied to composed revenue. */
const validationProfileFor = (
  platform: PaidMediaOverviewPlatform,
  apiPlatforms: PaidMediaOverviewRevenueValidationPlatform[]
): MockValidationProfile => {
  const fallback = MOCK_VALIDATION_PROFILES[platform] ?? { overReporting: 0.1, refundRate: 0.06 };
  const live = apiPlatforms?.find(entry => entry?.platform === platform);

  const reported = finite(live?.platform_reported_revenue);
  const gross = finite(live?.shopify_gross_revenue);
  const net = finite(live?.shopify_net_revenue);

  if (reported === null || gross === null || reported <= 0 || gross <= 0) {
    return fallback;
  }

  return {
    overReporting: Math.min(0.9, Math.max(0, (reported - gross) / reported)),
    refundRate: net !== null && net > 0 ? Math.min(0.9, Math.max(0, (gross - net) / gross)) : fallback.refundRate,
  };
};

export function composeRevenueValidation(
  aggregates: PlatformAggregate[],
  attributionMode: PaidMediaOverviewAttributionMode,
  apiPlatforms: PaidMediaOverviewRevenueValidationPlatform[]
): PaidMediaOverviewRevenueValidationData {
  return {
    platforms: aggregates.map(aggregate => {
      const profile = validationProfileFor(aggregate.platform, apiPlatforms);

      const platformReported =
        attributionMode === "shopify_validated"
          ? round2(aggregate.revenue / (1 - profile.overReporting))
          : aggregate.revenue;
      const storeGross = round2(platformReported * (1 - profile.overReporting));
      const storeNet = round2(storeGross * (1 - profile.refundRate));

      return {
        platform: aggregate.platform,
        available: true,
        platform_reported_revenue: platformReported,
        formatted_platform_reported_revenue: null,
        shopify_gross_revenue: storeGross,
        formatted_shopify_gross_revenue: null,
        shopify_net_revenue: storeNet,
        formatted_shopify_net_revenue: null,
        reporting_variance: percent(platformReported - storeGross, storeGross),
        formatted_reporting_variance: null,
        refund_adjusted_roas: ratio(storeNet, aggregate.spend),
        formatted_refund_adjusted_roas: null,
      };
    }),
  };
}

/* ---------------- attribution confidence ---------------- */

export function composeAttributionConfidence(
  aggregates: PlatformAggregate[],
  apiData: PaidMediaOverviewAttributionConfidenceData | null
): PaidMediaOverviewAttributionConfidenceData {
  if (finite(apiData?.attribution_confidence) !== null) {
    return apiData as PaidMediaOverviewAttributionConfidenceData;
  }

  const totals = totalsOf(aggregates);
  const mock = buildMockAttributionConfidence(totals.purchases);

  const component = (orders: number, weight: number) => ({
    orders,
    formatted_orders: null,
    percentage: percent(orders, mock.eligibleOrders),
    formatted_percentage: null,
    weight,
  });

  return {
    eligible_paid_media_orders: mock.eligibleOrders,
    formatted_eligible_paid_media_orders: null,
    attribution_confidence: mock.confidence,
    formatted_attribution_confidence: null,
    confidence_label: null,
    components: {
      recognized_source: component(mock.recognizedSource, 40),
      campaign_id: component(mock.campaignId, 25),
      ad_id: component(mock.adId, 15),
      usable_utm: component(mock.usableUtm, 20),
    },
  };
}

/* ---------------- campaigns ---------------- */

const toCampaignRow = (
  row: MockCampaignRow,
  totals: PeriodTotals,
  benchmarks: { roas: number | null; cpa: number | null }
): PaidMediaOverviewCampaignRow => {
  const spendShare = share(row.spend, totals.spend);
  const revenueShare = share(row.revenue, totals.revenue);

  return {
    platform: row.platform,
    campaign_id: row.campaignId,
    campaign_name: row.campaignName,
    objective_or_network: row.objectiveOrNetwork,
    start_date: row.startDate,
    spend: row.spend,
    formatted_spend: null,
    impressions: row.impressions,
    formatted_impressions: null,
    clicks: row.clicks,
    formatted_clicks: null,
    ctr: percent(row.clicks, row.impressions),
    formatted_ctr: null,
    purchases: row.purchases,
    formatted_purchases: null,
    revenue: row.revenue,
    formatted_revenue: null,
    roas: row.roas,
    formatted_roas: null,
    cost_per_purchase: ratio(row.spend, row.purchases),
    formatted_cost_per_purchase: null,
    spend_share: spendShare,
    formatted_spend_share: null,
    revenue_share: revenueShare,
    formatted_revenue_share: null,
    efficiency_index:
      spendShare !== null && revenueShare !== null && spendShare > 0
        ? round2(revenueShare / spendShare)
        : null,
    formatted_efficiency_index: null,
    status_code: row.statusCode,
    status_label: null,
    status_reason: null,
    data_sufficiency: "SUFFICIENT",
    benchmark_roas: benchmarks.roas,
    formatted_benchmark_roas: null,
    benchmark_cpa: benchmarks.cpa,
    formatted_benchmark_cpa: null,
  };
};

/**
 * Live rows plus mocked rows for the platforms the API doesn't serve, with
 * shares and benchmarks recomputed across all four platforms.
 */
export function composeCampaignRows(
  context: OverviewCompositionContext,
  universe: MockCampaignUniverse,
  apiRows: PaidMediaOverviewCampaignRow[],
  search?: string
): PaidMediaOverviewCampaignRow[] {
  const apiPlatforms = new Set(
    (apiRows ?? [])
      .map(row => row?.platform)
      .filter((platform): platform is PaidMediaOverviewPlatform => Boolean(platform))
  );

  const mockRows = filterMockCampaignRows(universe.rows, context, search).filter(
    row => !apiPlatforms.has(row.platform)
  );

  const liveRows = (apiRows ?? []).filter(row => Boolean(row?.campaign_id));

  const totals: PeriodTotals = {
    spend: round2(
      liveRows.reduce((sum, row) => sum + (apiMoney(row.spend) ?? 0), 0) +
        mockRows.reduce((sum, row) => sum + row.spend, 0)
    ),
    revenue: round2(
      liveRows.reduce((sum, row) => sum + (apiMoney(row.revenue) ?? 0), 0) +
        mockRows.reduce((sum, row) => sum + row.revenue, 0)
    ),
    purchases:
      liveRows.reduce((sum, row) => sum + (finite(row.purchases) ?? 0), 0) +
      mockRows.reduce((sum, row) => sum + row.purchases, 0),
  };

  const benchmarks = {
    roas: ratio(totals.revenue, totals.spend),
    cpa: ratio(totals.spend, totals.purchases),
  };

  const normalizedLiveRows = liveRows.map(row => ({
    ...row,
    spend: apiMoney(row.spend),
    revenue: apiMoney(row.revenue),
    cost_per_purchase: apiMoney(row.cost_per_purchase),
    formatted_spend: null,
    formatted_revenue: null,
    formatted_impressions: null,
    formatted_clicks: null,
    formatted_ctr: null,
    formatted_purchases: null,
    formatted_roas: null,
    formatted_cost_per_purchase: null,
    formatted_spend_share: null,
    formatted_revenue_share: null,
    formatted_efficiency_index: null,
    spend_share: share(apiMoney(row.spend) ?? 0, totals.spend),
    revenue_share: share(apiMoney(row.revenue) ?? 0, totals.revenue),
    benchmark_roas: benchmarks.roas,
    benchmark_cpa: benchmarks.cpa,
  }));

  return [
    ...normalizedLiveRows,
    ...mockRows.map(row => toCampaignRow(row, totals, benchmarks)),
  ];
}

const SORT_ACCESSORS: Record<string, (_row: PaidMediaOverviewCampaignRow) => number | string> = {
  revenue: row => finite(row.revenue) ?? 0,
  spend: row => finite(row.spend) ?? 0,
  purchases: row => finite(row.purchases) ?? 0,
  roas: row => finite(row.roas) ?? 0,
  impressions: row => finite(row.impressions) ?? 0,
  clicks: row => finite(row.clicks) ?? 0,
  campaign_name: row => row.campaign_name ?? "",
};

export function composeCampaignsPage(
  rows: PaidMediaOverviewCampaignRow[],
  options: {
    sortBy: string;
    sortOrder: PaidMediaOverviewSortOrder;
    page: number;
    pageSize: number;
  }
): PaidMediaOverviewCampaignsData {
  const accessor = SORT_ACCESSORS[options.sortBy] ?? SORT_ACCESSORS.revenue;
  const direction = options.sortOrder === "asc" ? 1 : -1;

  const sorted = [...rows].sort((left, right) => {
    const leftValue = accessor(left);
    const rightValue = accessor(right);
    if (typeof leftValue === "string" || typeof rightValue === "string") {
      return String(leftValue).localeCompare(String(rightValue)) * direction;
    }
    return (leftValue - rightValue) * direction;
  });

  const pageSize = Math.max(1, options.pageSize);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const page = Math.min(Math.max(1, options.page), totalPages);
  const start = (page - 1) * pageSize;

  const spends = sorted.map(row => finite(row.spend) ?? 0);
  const roasValues = sorted
    .map(row => finite(row.roas))
    .filter((value): value is number => value !== null);

  const benchmarks: PaidMediaOverviewCampaignBenchmarks = {
    spend_p75: percentile(spends, 0.75),
    spend_median: percentile(spends, 0.5),
    revenue_p75: percentile(
      sorted.map(row => finite(row.revenue) ?? 0),
      0.75
    ),
    purchases_p75: percentile(
      sorted.map(row => finite(row.purchases) ?? 0),
      0.75
    ),
    roas_p75: percentile(roasValues, 0.75),
    roas_p25: percentile(roasValues, 0.25),
    target_roas: sorted[0]?.benchmark_roas ?? null,
    cpa_p75: percentile(
      sorted
        .map(row => finite(row.cost_per_purchase))
        .filter((value): value is number => value !== null),
      0.75
    ),
    target_cpa: sorted[0]?.benchmark_cpa ?? null,
    benchmark_source: "blended_period_benchmarks",
  };

  return {
    rows: sorted.slice(start, start + pageSize),
    benchmarks,
    total_rows: sorted.length,
    page,
    page_size: pageSize,
    total_pages: totalPages,
  };
}

/* ---------------- campaign detail ---------------- */

export function composeMockCampaignDetail(
  platform: PaidMediaOverviewPlatform,
  campaignId: string,
  context: OverviewCompositionContext
): PaidMediaOverviewCampaignDetailData | null {
  const seed = MOCK_CAMPAIGN_SEEDS.find(
    candidate => candidate.platform === platform && candidate.campaignId === campaignId
  );
  if (!seed) {
    return null;
  }

  const scale = attributionScale(platform, context.attributionMode);
  const daily = buildMockCampaignDailyFacts(
    platform,
    campaignId,
    context.startDate,
    context.endDate
  ).map(fact => ({
    ...fact,
    purchases: Math.round(fact.purchases * scale),
    revenue: round2(fact.revenue * scale),
  }));

  if (!daily.length) {
    return null;
  }

  const totals: MockTotals = sumMockFacts(daily);

  return {
    header: {
      platform,
      campaign_id: campaignId,
      campaign_name: seed.campaignName,
      objective_or_network: seed.objectiveOrNetwork,
      start_date: seed.startDate,
      end_date: null,
    },
    kpis: {
      spend: totals.spend,
      formatted_spend: null,
      revenue: totals.revenue,
      formatted_revenue: null,
      purchases: totals.purchases,
      formatted_purchases: null,
      impressions: totals.impressions,
      formatted_impressions: null,
      clicks: totals.clicks,
      formatted_clicks: null,
      ctr: percent(totals.clicks, totals.impressions),
      formatted_ctr: null,
      roas: ratio(totals.revenue, totals.spend),
      formatted_roas: null,
      cost_per_purchase: ratio(totals.spend, totals.purchases),
      formatted_cost_per_purchase: null,
    },
    trend: {
      granularity: context.granularity,
      series: bucketMockFacts(daily, context.granularity).map(fact => ({
        date: fact.date,
        spend: fact.spend,
        formatted_spend: null,
        revenue: fact.revenue,
        formatted_revenue: null,
      })),
    },
    insight: null,
  };
}

/** Live campaign detail, converted into the currency the drawer renders. */
export function convertCampaignDetailMoney(
  detail: PaidMediaOverviewCampaignDetailData | null
): PaidMediaOverviewCampaignDetailData | null {
  if (!detail) {
    return null;
  }

  return {
    ...detail,
    kpis: detail.kpis
      ? {
          ...detail.kpis,
          spend: apiMoney(detail.kpis.spend),
          revenue: apiMoney(detail.kpis.revenue),
          cost_per_purchase: apiMoney(detail.kpis.cost_per_purchase),
        }
      : null,
    trend: detail.trend
      ? {
          ...detail.trend,
          series: (detail.trend.series ?? []).map(point => ({
            ...point,
            spend: apiMoney(point.spend),
            revenue: apiMoney(point.revenue),
          })),
        }
      : null,
  };
}

/* ---------------- filters ---------------- */

/** Live filter options completed with every platform and mocked campaign. */
export function composeOverviewFilters(
  apiFilters: PaidMediaOverviewFiltersData | null,
  universe: MockCampaignUniverse,
  statusOptions: string[]
): PaidMediaOverviewFiltersData {
  const platforms = OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => {
    const apiOption = apiFilters?.platforms?.find(option => option?.value === descriptor.key);
    return {
      value: descriptor.key,
      // Labels are localised in the component through the descriptor, so the
      // API label is only a fallback for an unexpected platform.
      label: apiOption?.label ?? descriptor.key,
      available: true,
    };
  });

  const campaigns = [
    ...(apiFilters?.campaigns ?? []).filter(campaign => Boolean(campaign?.campaign_id)),
    ...universe.rows.map(row => ({
      platform: row.platform,
      campaign_id: row.campaignId,
      campaign_name: row.campaignName,
    })),
  ];

  const seenCampaigns = new Set<string>();
  const dedupedCampaigns = campaigns.filter(campaign => {
    if (seenCampaigns.has(campaign.campaign_id)) {
      return false;
    }
    seenCampaigns.add(campaign.campaign_id);
    return true;
  });

  const objectives = Array.from(
    new Set([
      ...(apiFilters?.objectives_or_networks ?? []).filter(Boolean),
      ...universe.rows.map(row => row.objectiveOrNetwork),
    ])
  ).sort((left, right) => left.localeCompare(right));

  const statuses = Array.from(
    new Set([
      ...(apiFilters?.statuses ?? [])
        .map(status => status?.value)
        .filter((value): value is string => Boolean(value)),
      ...statusOptions,
    ])
  ).map(value => ({ value, label: value }));

  return {
    platforms,
    campaigns: dedupedCampaigns,
    objectives_or_networks: objectives,
    statuses,
  };
}

export function mockCampaignStatusCodes(universe: MockCampaignUniverse): string[] {
  return Array.from(new Set(universe.rows.map(row => row.statusCode)));
}

/** True for a platform (or platform filter) served only by the mock dataset. */
export function isMockOnlyPlatform(
  platform: PaidMediaOverviewPlatform | PaidMediaOverviewPlatformFilter | null | undefined
): boolean {
  if (!platform || platform === "all") {
    return false;
  }
  return overviewPlatformDescriptor(platform)?.mockOnly === true;
}
